import React, {useState} from 'react';
import {Button, Checkbox, Icon, Label, Modal} from 'semantic-ui-react';
import {useViewer} from '../state/viewer/hooks';
import {navigateTo} from '../util/history';
import {Link} from '../util/links';
import {
  AccessOptions,
  getDescriptionForProjectAccess,
  getNameForProjectAccess,
  getTooltipTextForProjectAccess,
} from '../util/permissions';
import makeComp from '../util/profiler';
import * as urls from '../util/urls';
import LegacyWBIcon from './elements/LegacyWBIcon';
import * as S from './ProjectAccess.styles';

const accessMatches = (accessA: AccessOptions, accessB: AccessOptions) => {
  return (
    accessA === accessB ||
    (accessA !== 'USER_READ' &&
      accessA !== 'USER_WRITE' &&
      accessB !== 'USER_READ' &&
      accessB !== 'USER_WRITE')
  );
};

export interface ProjectAccessOptionProps {
  name: string;
  icon: string;
  access: AccessOptions;
  description: string;
  disabled: boolean;
  warning: boolean;
  default: boolean;
  selected: boolean;
  advanced?: boolean;
  select(): void;
}

export interface ProjectAccessInput {
  projectAccess: AccessOptions;
  defaultAccess: AccessOptions;
  privateOnly: boolean;
  isTeam: boolean;
  selectAccess(access: string): void;
}

export function projectAccessOptions(
  input: ProjectAccessInput
): ProjectAccessOptionProps[] {
  const {projectAccess, defaultAccess, privateOnly, isTeam, selectAccess} =
    input;

  const privateOption: ProjectAccessOptionProps = {
    name: getNameForProjectAccess(`ENTITY_READ`, isTeam),
    icon: 'lock',
    access: 'ENTITY_READ',
    description: getDescriptionForProjectAccess(`ENTITY_READ`, isTeam),
    disabled: false,
    warning: false,
    default: accessMatches('ENTITY_READ', defaultAccess),
    selected: accessMatches('ENTITY_READ', projectAccess),
    advanced: false,
    select: () => selectAccess('ENTITY_READ'),
  };

  const publicOption: ProjectAccessOptionProps = {
    name: getNameForProjectAccess(`USER_READ`, isTeam),
    icon: 'lock-open',
    access: 'USER_READ',
    description: getDescriptionForProjectAccess(`USER_READ`, isTeam),
    disabled: isTeam && privateOnly,
    warning: isTeam,
    default: accessMatches('USER_READ', defaultAccess),
    selected: accessMatches('USER_READ', projectAccess),
    advanced: false,
    select: () => selectAccess('USER_READ'),
  };

  const openOption: ProjectAccessOptionProps = {
    name: getNameForProjectAccess(`USER_WRITE`, isTeam),
    icon: 'open-project',
    access: 'USER_WRITE',
    description: getDescriptionForProjectAccess(`USER_WRITE`, isTeam),
    disabled: isTeam && privateOnly,
    warning: isTeam,
    default: accessMatches('USER_WRITE', defaultAccess),
    selected: accessMatches('USER_WRITE', projectAccess),
    advanced: true,
    select: () => selectAccess('USER_WRITE'),
  };

  return [privateOption, publicOption, openOption];
}

export const ProjectAccessOption: React.FC<ProjectAccessOptionProps> = makeComp(
  ({
    name,
    icon,
    access,
    description,
    disabled,
    warning,
    default: optionDefault,
    selected,
    select,
  }) => {
    return (
      <Checkbox
        radio
        label={
          <label>
            <div className="project-access--modal-option-title">
              <LegacyWBIcon className="lock-icon" name={icon} />
              {name}
              {warning && <Icon name="warning sign" color="red" />}
              {optionDefault && <Label color="grey">DEFAULT</Label>}
            </div>
            <div className="project-access--modal-option-description">
              {description}
            </div>
          </label>
        }
        value={access}
        key={access}
        checked={selected}
        disabled={disabled}
        readOnly={disabled}
        className="project-access--modal-option"
        onClick={disabled ? undefined : select}
      />
    );
  },
  {id: 'ProjectAccessOption', memo: true}
);

export interface ProjectAccessProject {
  access: AccessOptions;
  entity: {
    id: string;
    name: string;
    readOnlyAdmin: boolean;
    defaultAccess: AccessOptions;
    privateOnly: boolean;
    isTeam: boolean;
  };
}

export interface ProjectAccessProps {
  project: ProjectAccessProject;
  compact?: boolean;
  updateProjectAccess(access: AccessOptions): void;
}

// This is the project access level icon (locked or unlocked) and its popup,
// including the option to change the access level (if you have permission)

const ProjectAccess: React.FC<ProjectAccessProps> = makeComp(
  ({project, compact, updateProjectAccess}) => {
    const {
      entity: {
        id: entityID,
        name: entityName,
        readOnlyAdmin,
        isTeam,
        privateOnly,
        defaultAccess,
      },
    } = project;
    // access can be null for older projects (before we added the private projects flag)
    // see availableOptions() in ProjectEditor.js
    // TODO: data migration to fix this in the db!
    // TODO: Should change the type to reflect that this can be null
    const projectAccess = convertOldAccess(project.access || defaultAccess);

    const viewer = useViewer();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedAccess, setSelectedAccess] =
      useState<AccessOptions>(projectAccess);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(
      projectAccess === 'USER_WRITE'
    );
    const [tooltipOpen, setTooltipOpen] = useState(false);

    let accessBadgeText: string;
    if (projectAccess === 'USER_READ') {
      accessBadgeText = 'PUBLIC';
    } else if (projectAccess === 'USER_WRITE') {
      accessBadgeText = 'OPEN';
    } else if (isTeam) {
      accessBadgeText = 'TEAM';
    } else {
      accessBadgeText = 'PRIVATE';
    }

    const options = projectAccessOptions({
      projectAccess: selectedAccess,
      defaultAccess,
      privateOnly,
      isTeam,
      selectAccess: (access: AccessOptions) => setSelectedAccess(access),
    });

    const currentOption = options.find(
      option => option.access === projectAccess
    );
    const selectedOption = options.find(
      option => option.access === selectedAccess
    );
    const disabled = selectedOption ? selectedOption.disabled : false;

    const viewerIsTeamMember =
      viewer?.teams.edges.some(e => e.node.id === entityID) ?? false;
    const canEdit =
      !readOnlyAdmin || (isTeam && viewerIsTeamMember && !privateOnly);

    const onIndicatorClick = () => {
      if (!canEdit) {
        return;
      }
      setModalOpen(true);
      setTooltipOpen(false);
      setSelectedAccess(projectAccess);
    };

    const projectInPersonalEntity = !isTeam && viewer?.username === entityName;

    return (
      <>
        {compact ? (
          <S.TooltipPopup
            open={tooltipOpen}
            offset={3}
            onOpen={() => setTooltipOpen(true)}
            onClose={() => setTooltipOpen(false)}
            position={`top center`}
            popperModifiers={{
              preventOverflow: {
                boundariesElement: 'viewport',
              },
            }}
            trigger={
              <span
                className="project-access--indicator"
                onClick={onIndicatorClick}>
                {currentOption != null && (
                  <LegacyWBIcon
                    className="lock-icon"
                    name={currentOption.icon}
                  />
                )}
              </span>
            }>
            {getTooltipTextForProjectAccess(projectAccess, isTeam)}
          </S.TooltipPopup>
        ) : (
          <div className="project-access">
            <Label
              as="a"
              size="medium"
              className={`project-access--badge ${accessBadgeText.toLowerCase()}`}
              onClick={onIndicatorClick}>
              {currentOption != null && (
                <LegacyWBIcon className="lock-icon" name={currentOption.icon} />
              )}
              {accessBadgeText}
            </Label>
          </div>
        )}
        <Modal
          open={modalOpen}
          className="project-access--modal"
          onClose={() => setModalOpen(false)}>
          <Modal.Header>Project Settings</Modal.Header>
          <Modal.Content>
            <p>
              Privacy settings affect your whole project, including runs,
              reports, artifacts, etc.
            </p>
            {options.map(option => {
              if (option.advanced) {
                // hide open option by default
                if (!showAdvancedSettings) {
                  return <React.Fragment key="advanced"></React.Fragment>;
                }
              }
              return <ProjectAccessOption key={option.access} {...option} />;
            })}
            {!showAdvancedSettings && (
              <span
                className="show-advanced-settings-button"
                onClick={() => setShowAdvancedSettings(true)}>
                + Show advanced settings
              </span>
            )}
            {isTeam && privateOnly && (
              <S.TeamSettingsPrompt>
                To make this project Public or Open, turn off the “Force all
                projects in this team to be private” setting in{' '}
                <Link to={urls.teamSettings(entityName)}>Team Settings</Link>.
              </S.TeamSettingsPrompt>
            )}
            {projectInPersonalEntity && <CreateTeamPrompt />}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              primary
              disabled={disabled}
              onClick={() => {
                updateProjectAccess(selectedAccess);
                setModalOpen(false);
              }}>
              Save
            </Button>
          </Modal.Actions>
        </Modal>
      </>
    );
  },
  {id: 'ProjectAccess', memo: true}
);

export default ProjectAccess;

const CreateTeamPrompt: React.FC = makeComp(
  () => {
    return (
      <S.CreateTeamPrompt>
        <S.CreateTeamText>
          Create a team to collaborate on private projects.
        </S.CreateTeamText>
        <S.CreateTeamButton
          primary
          basic
          onClick={() => {
            window.analytics?.track('Create Team Started', {
              location: 'project-settings',
            });
            navigateTo({pathname: '/create-team'});
          }}>
          Create Team
        </S.CreateTeamButton>
      </S.CreateTeamPrompt>
    );
  },
  {id: 'CreateTeamPrompt', memo: true}
);

function convertOldAccess(access: AccessOptions): AccessOptions {
  if (access === 'PRIVATE') {
    return 'ENTITY_READ';
  }
  return access;
}
